'use client';

import * as React from 'react';
import { t } from 'i18next';
import { format, isAfter, startOfDay } from 'date-fns';
import { DateRange } from 'react-day-picker';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FormControl, FormHelperText } from '../shared/FormControl';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { BaseTooltip } from '../../Tooltip';
import { CardDropdown } from '../../Dropdown/CardDropdown';
import { DateRangePreset } from '@/utils/shared/datePicker/dateRangePresets';
import { Icon } from '@/components/shared/display/Icon';
import { CalendarIcon } from '@/assets/Icons';

export interface BaseDateRangePickerProps {
  label?: string;
  helperText?: string;
  error?: boolean;
  required?: boolean;
  disabled?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  customTrigger?: React.ReactNode;
  mode?: 'single' | 'range';
  classNames?: {
    button?: string;
    label?: string;
    helperText?: string;
    startAdornment?: string;
    endAdornment?: string;
    date?: string;
    wrapper?: string;
  };
  numberOfMonths?: number;
  dateFormat?: string;
  defaultLabel?: string;
  showLabel?: boolean;
  from?: Date;
  to?: Date;
  defaultFrom?: Date;
  variant?: 'input' | 'button';
  limitLabel?: string;
  dateRangePresets?: DateRangePreset[];
  onDateUpdate?: (date: Date | DateRange | undefined) => void;
  onOpen?: (open: boolean) => void;
  customDatesRangeLimits?: (obj: any) => boolean;
}

export function BaseDateRangePicker({
  label = 'Dates Range',
  variant = 'input',
  helperText,
  error = false,
  required = false,
  disabled = false,
  startAdornment,
  endAdornment = <Icon icon={CalendarIcon} className="mr-2 h-4 w-4" />,
  numberOfMonths = 2,
  classNames,
  dateFormat = 'dd.MM.yyyy',
  defaultLabel = 'Pick a date range',
  mode = 'range',
  customTrigger,
  showLabel = true,
  defaultFrom,
  from,
  to,
  limitLabel,
  dateRangePresets,
  customDatesRangeLimits,
  onDateUpdate,
  onOpen,
}: BaseDateRangePickerProps) {
  const [date, setDate] = React.useState<Date | DateRange | undefined>(
    mode === 'single' ? new Date() : { from: defaultFrom || from, to: to || new Date() }
  );

  const [tempDate, setTempDate] = React.useState<Date | DateRange | undefined>(date);
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [month, setMonth] = React.useState<Date | undefined>();
  const [selectedDropdownOption, setSelectedDropdownOption] = React.useState<string | null>(null);
  const dropdownTitle = selectedDropdownOption
    ? t('general.presetRangeDropdown.showingDataFor', { date: selectedDropdownOption })
    : t('general.presetRangeDropdown.label');
  const dropdownRef = React.useRef<{ reset: () => void }>(null);

  React.useEffect(() => {
    if (onDateUpdate) onDateUpdate(date);
  }, [date]);

  const resetDropdown = () => {
    setSelectedDropdownOption(null);
    dropdownRef.current?.reset();
  };

  const handleDateSelection = (selectedDate: DateRange) => {
    if (selectedDate && !selectedDate.to && selectedDate.from)
      selectedDate = { ...selectedDate, to: selectedDate.from };
    if (selectedDate && !selectedDate.from && selectedDate.to)
      selectedDate = { ...selectedDate, from: selectedDate.to };

    setTempDate(selectedDate);
    resetDropdown();
  };

  const handleDoneClick = () => {
    setDate(tempDate);
    setIsPopoverOpen(false);
  };

  const handleCancelClick = () => {
    setTempDate(date);
    setMonth((date as DateRange)?.from);
    setIsPopoverOpen(false);
  };

  const handleResetClick = () => {
    setTempDate(undefined);
    resetDropdown();
  };

  const isDateDisabled = React.useCallback(
    (day: Date) => {
      const today = startOfDay(new Date());
      const startDate = (tempDate as DateRange)?.from;
      const endDate = (tempDate as DateRange)?.to;
      if (isAfter(day, today)) return true;
      if (customDatesRangeLimits) return customDatesRangeLimits({ startDate, endDate, day });
      return false;
    },
    [tempDate]
  );

  const dateField =
    mode === 'single'
      ? date
        ? format(date as Date, dateFormat)
        : defaultLabel
      : (date as DateRange)?.from
      ? (date as DateRange).to
        ? `${format((date as DateRange).from!, dateFormat)} - ${format((date as DateRange).to!, dateFormat)}`
        : format((date as DateRange).from!, dateFormat)
      : defaultLabel;

  return (
    <FormControl error={error} className={classNames?.wrapper}>
      <div className="relative">
        {variant === 'input' && showLabel && label && (
          <label className={cn('absolute left-4 transition-all pointer-events-none', classNames?.label)}>
            {label} {required && <span className="text-critical ml-1">*</span>}
          </label>
        )}
        <Popover
          onOpenChange={(open) => {
            if (!disabled) {
              setIsPopoverOpen(open);
              if (open) setTempDate(date); // Reset tempDate when opening
              onOpen && onOpen(open);
              setMonth((date as DateRange)?.from);
              setSelectedDropdownOption(null);
            }
          }}
          open={isPopoverOpen}
        >
          <BaseTooltip content={limitLabel} side="bottom" sideOffset={13} classNames={{ content: '' }}>
            <PopoverTrigger asChild>
              {customTrigger || variant === 'button' ? (
                <Button
                  className={cn(
                    'font-semibold text-sm h-10 flex items-center',
                    `${isPopoverOpen ? 'ring-1 ring-primary-600' : ''}`
                  )}
                  variant={'outline'}
                  data-testid="date_picker_button"
                >
                  <span>
                    <Icon icon={CalendarIcon} />
                  </span>
                  {dateField}
                  {isPopoverOpen ? <ChevronUp /> : <ChevronDown />}
                </Button>
              ) : (
                <Button variant={'datepicker'} className={classNames?.button} disabled={disabled}>
                  <span className={`${startAdornment && 'pl-4'}`}>{dateField}</span>
                </Button>
              )}
            </PopoverTrigger>
          </BaseTooltip>

          {!disabled && (
            <PopoverContent className="w-auto p-0 mx-2" align="start">
              {dateRangePresets && (
                <div className="w-full flex justify-between p-3">
                  <div className="capitalize text-sm">
                    <CardDropdown
                      ref={dropdownRef}
                      fixedTitle={dropdownTitle}
                      allowNoDefaultSelection
                      items={dateRangePresets}
                      onSelect={(data) => {
                        const range = data.getRange();
                        setTempDate(range);
                        setMonth((range as DateRange)?.from);
                        setSelectedDropdownOption(data.label);
                      }}
                    />
                  </div>
                </div>
              )}
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={(tempDate as DateRange)?.from}
                month={month}
                onMonthChange={setMonth}
                selected={tempDate as DateRange}
                onSelect={(selectedDate) => handleDateSelection(selectedDate as DateRange)}
                numberOfMonths={numberOfMonths}
                disabled={isDateDisabled}
              />
              <div className="w-full flex items-center p-3 justify-between">
                <Button
                  size={'sm'}
                  variant="filled"
                  className="bg-primary-100 hover:bg-primary-100 hover:text-default text-default disabled:text-white"
                  onClick={handleResetClick}
                  disabled={!tempDate}
                >
                  Clear
                </Button>
                <div>
                  <Button size={'sm'} variant="default" onClick={handleCancelClick}>
                    Cancel
                  </Button>
                  <Button size={'sm'} variant="filled" onClick={handleDoneClick} disabled={!tempDate}>
                    Apply
                  </Button>
                </div>
              </div>
            </PopoverContent>
          )}
        </Popover>
      </div>
      {helperText && (
        <FormHelperText className={cn({ 'text-critical': error }, classNames?.helperText)} error={error}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}
